// context/AdminStatusContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "../backend/firebaseconfig"; //  ensure this path/casing matches your project
import { useUserRole } from "../context/UserRoleContext";
import { useUserEmail } from "../context/UserEmailContext";

// If you want the hook guard to actually throw when missing provider,
// set the default to undefined and add a throw in the hook.
// const AdminStatusContext = createContext(undefined);
const AdminStatusContext = createContext({
  adminStatus: [],
  setAdminStatus: () => {},
  refreshAdminStatus: async () => {},
  loading: false,
  error: null,
});

export const AdminStatusProvider = ({ children }) => {
  const { userRole } = useUserRole();
  const { userEmail } = useUserEmail();

  const [adminStatus, setAdminStatus] = useState(() => {
    try {
      const stored = localStorage.getItem("adminStatus");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // to avoid setState on unmounted
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const refreshAdminStatus = useMemo(
    () => async () => {
      if (userRole !== "Administrators" || !userEmail) return;
      setLoading(true);
      setError(null);
      try {
        const adminsCol = collection(
          firestore,
          "Authorized Members/Administrators/Data"
        );
        const adminQuery = query(adminsCol, where("email", "==", userEmail));
        const snap = await getDocs(adminQuery);

        if (!mountedRef.current) return;

        if (!snap.empty) {
          const data = snap.docs[0].data();
          const status = Array.isArray(data?.status) ? data.status : [];
          setAdminStatus(status);
        } else {
          setAdminStatus([]);
        }
      } catch (e) {
        if (!mountedRef.current) return;
        // console.error("Error fetching admin status:", e);
        setError(e);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [userRole, userEmail]
  );

  // auto-fetch when role/email change
  useEffect(() => {
    refreshAdminStatus();
  }, [refreshAdminStatus]);

  // if user is no longer admin, clear status to avoid stale permissions
  useEffect(() => {
    if (userRole !== "Administrators" && adminStatus.length) {
      setAdminStatus([]);
    }
  }, [userRole]); // eslint-disable-line react-hooks/exhaustive-deps

  // sync localStorage
  useEffect(() => {
    // console.log("Admin status updated:", adminStatus);
    try {
      if (adminStatus?.length) {
        localStorage.setItem("adminStatus", JSON.stringify(adminStatus));
      } else {
        localStorage.removeItem("adminStatus");
      }
    } catch {}
  }, [adminStatus]);

  const value = useMemo(
    () => ({
      adminStatus,
      setAdminStatus,
      refreshAdminStatus,
      loading,
      error,
    }),
    [adminStatus, loading, error, refreshAdminStatus]
  );

  return (
    <AdminStatusContext.Provider value={value}>
      {children}
    </AdminStatusContext.Provider>
  );
};

export const useAdminStatus = () => {
  const ctx = useContext(AdminStatusContext);
  // If you switch to `createContext(undefined)`, uncomment the guard:
  // if (!ctx) {
  //   throw new Error("useAdminStatus must be used within an AdminStatusProvider");
  // }
  return ctx;
};
